/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { defineComponent } from 'vue'

/**************************************************************************************************/
/** @import Components/Global                                                                    **/
/**************************************************************************************************/
import * as BasicHeader from '@/Components/Global/BasicHeader/template.vue'
import * as Spinner from '@/Components/Global/Spinner/template.vue'
import * as ModuleBox from '@/Components/Global/ModuleBox/template.vue'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { AppModules } from '@/Constants/AppModules'
import { AppValues } from '@/Constants/AppValues'
import { Breakpoints } from '@/Constants/Breakpoints'
import { VueRouter } from '@/Constants/VueRouter'
import { Vuex } from '@/Constants/Vuex'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { StoreHelper } from '@/Helpers/Utils/StoreHelper'
import { TypesHelper } from '@/Helpers/Utils/TypesHelper'

/**************************************************************************************************/
/** @import Mixins                                                                               **/
/**************************************************************************************************/
import MixinBase from '@/Mixins/MixinBase'
import MixinComponent from '@/Mixins/MixinComponent'
import MixinFetch from '@/Mixins/MixinFetch'
import MixinResponsive from '@/Mixins/MixinResponsive'

/**************************************************************************************************/
/** @import Store/Global                                                                         **/
/**************************************************************************************************/
import Store from '@/Store/Global/Default'

/**************************************************************************************************/
/** @internal Type @name System                                                                  **/
/**************************************************************************************************/
type System = { _idSystem: string }

/**************************************************************************************************/
/** @internal Const @name InternalModulesView                                                    **/
/**************************************************************************************************/
const InternalModulesView = defineComponent(
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	name: VueRouter.Views.Modules.INTERNAL_CPANEL.NAME, mixins: [MixinBase, MixinComponent, MixinFetch, MixinResponsive],

	components:
	{
		BasicHeader: BasicHeader.default,
		ModuleBox: ModuleBox.default,
		Spinner: Spinner.default
	},

	props:
	{
		unauthorizedRedirect: Boolean
	},

	/*************************************************/
	/** @Data                                       **/
	/*************************************************/
	data()
	{
		return {
			permissions:
			{
				[AppValues.ObjectIds.Modules.ADMINISTRATION.toUpperCase()]: false,
				[AppValues.ObjectIds.Modules.CHECKLISTS.toUpperCase()]: false,
				[AppValues.ObjectIds.Modules.SERVICES.toUpperCase()]: false,
				[AppValues.ObjectIds.Modules.SETUP_CHECKLIST.toUpperCase()]: false,
				[AppValues.ObjectIds.Modules.STATISTICS.toUpperCase()]: false,
				[AppValues.ObjectIds.Modules.MANAGE_CHECKLIST.toUpperCase()]: false
			} as Record<string, boolean>
		}
	},

	/*************************************************/
	/** @Hooks                                      **/
	/*************************************************/
	created() : void
	{
		// Validación para la Redirección no Autorizada.
		const { unauthorizedRedirect } = this.$props
		if (unauthorizedRedirect) this.ShowToast('Acceso No Autorizado', 'No tienes permisos para acceder al Sistema.', 'danger')

		// Obtención de Permisos y Modulos (Almacenados en Store).
		Store.dispatch('fetchPermissions')
		Store.dispatch('fetchSystems')
	},

	mounted() : void
	{
		if (this.$route.params.successLogin === 'true')
		{
			const { name, pLastName } = Store.getters.getStoredUser
			this.ShowToast(`Bienvenid@, ${ name } ${ pLastName }`, 'Has iniciado sesión correctamente!', 'success')
		}
		Store.commit('updateViewsViewMode', VueRouter.ViewModes.INTERNAL)
		this.InitPermissions()
	},

	/*************************************************/
	/** @Computed                                   **/
	/*************************************************/
	computed:
	{
		BasicHeader(): BasicHeader.BasicHeaderRef
		{
			return this.$refs.basicHeader as BasicHeader.BasicHeaderRef
		},

		GetSystems() : Array<any>
		{
			// Los Sistemas se almacenarán todos juntos en una misma propiedad.
			const storedSystems: Array<any> = Store.getters.getStoredSystems

			// El filtro será aplicado directamente en la vista, validando que exista al menos un sistema en el array.
			if (!TypesHelper.isArrayInvalidOrEmpty(storedSystems)) {
				// Obtener los Sistemas correspondientes a la constante de los Clientes.
				const systemsIds = Object.keys(AppModules.Global.InternalModulesList)
				
				// Array que debe contener solamente los Sistemas de los Ids extraidos de la constante.
				return TypesHelper.getArrayFromStackByProperty(storedSystems, systemsIds, '_idSystem').sort((a: any, b: any) => {
					const $a = AppModules.Global.InternalModulesList[a._idSystem]?._params.index
					const $b = AppModules.Global.InternalModulesList[b._idSystem]?._params.index
					return ($a > $b)  ? 1 : -1
				})
			}

			return []
		}
	},

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		GetSystemParam(system: System, property: string) : boolean | number
		{
			return AppModules.Global.InternalModulesList[system._idSystem]?._params[property]
		},

		GetSystemProperty(system: System, property: string) : any
		{
			return (AppModules.Global.InternalModulesList[system._idSystem] as any)?.[property]
		},

		HasPermissionToSystem(system: System) : boolean
		{
			return this.permissions[system._idSystem.toUpperCase()]
		},

		InitPermissions() : void
		{
			const userPermissions = Store.getters.getStoredUserPermissionsAsObject
			this.permissions[AppValues.ObjectIds.Modules.ADMINISTRATION.toUpperCase()]   = userPermissions.ACCESS_MODULE_20?.privileges.read
			this.permissions[AppValues.ObjectIds.Modules.CHECKLISTS.toUpperCase()]       = userPermissions.ACCESS_MODULE_40?.privileges.read
			this.permissions[AppValues.ObjectIds.Modules.SERVICES.toUpperCase()]         = userPermissions.ACCESS_MODULE_30?.privileges.read
			this.permissions[AppValues.ObjectIds.Modules.SETUP_CHECKLIST.toUpperCase()]  = userPermissions.ACCESS_MODULE_41?.privileges.read
			this.permissions[AppValues.ObjectIds.Modules.STATISTICS.toUpperCase()]       = true
			this.permissions[AppValues.ObjectIds.Modules.MANAGE_CHECKLIST.toUpperCase()] = true
		},

		OnResponsiveBreakpoint(breakpoint: number) : void
		{
			// Cambios que afectan el aspecto visual de los Componentes (según Breakpoint).
			this.BasicHeader.Set<BasicHeader.BasicHeaderRef['states']>({ isMobile: breakpoint <= Breakpoints.Medium })
		}
	},

	/*************************************************/
	/** @Watch                                      **/
	/*************************************************/
	watch:
	{
		...StoreHelper.watchStoreProperty(Vuex.Modules.Global.Names.Permissions, 'InitPermissions')
	}
})

/**************************************************************************************************/
/** @export Component @name InternalModulesView                                                  **/
/**************************************************************************************************/
export default InternalModulesView